import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { eventService } from '../../services/eventService';

import {
  designCreditCopy,
  designCreditShortfall,
  useDesignCreditStatus,
} from '../../hooks/useDesignCreditStatus';
import {
  getPhaseBuildBusyLabel,
  getPhaseBuildTitle,
  formatPhaseCredits,
  isPhaseBuildActionEnabled,
  usePhaseBuildState,
} from '../../hooks/usePhaseBuildState';
import { useProjectHasArchitecture } from '../../hooks/useProjectHasArchitecture';
import { useProjectHasGeneratedTasks } from '../../hooks/useProjectHasTasks';
import type { PhaseBuildExecutionState } from '../../services/buildPhaseService';
import { signalInsufficientCredits } from '../../services/creditGateEvents';
import type { DesignPagesResponse } from '../../services/designService';
import { registerPlanningAction } from '../../services/planningActionRegistry';
import { useGetPlanningStatusQuery } from '../../store/services/planningRuntimeApi';
import {
  useAcceptPendingRequirementsProposalMutation,
  useRejectPendingRequirementsProposalMutation,
} from '../../store/services/requirementsApi';
import {
  supervisorButtonResumePhase,
  type SupervisorButton,
} from '../../types/supervisorButtons';
import {
  getPhaseActionCreditShortfall,
  getCreditShortfall,
  getTopUpCreditAmount,
} from '../../utils/creditGate';
import {
  currentPhaseReadyMessage,
  formatPhaseReadyMessageForState,
  isPlanningTaskContractComplete,
  normalizePhaseReadyTaskSummary,
  phaseSelectionPrompt,
} from '../../utils/phaseBuildPresentation';
import { planningActionErrorMessage } from '../../utils/planningActionErrors';
import { hasPlanningTaskCredit, planningTaskCreditAmount } from '../../utils/planningTaskCredits';
import { BILLING_TOPUP_SUCCESS_EVENT, BILLING_UPGRADE_SUCCESS_EVENT, BillingModal } from '../BillingModal';
import { BrandPendulumMark } from '../BrandPendulumMark';
import {
  type BuildFlowResolvedDetail,
  isSuccessfulBuildResolution,
} from '../ChatBox/buildFlowResolution';
import {
  dispatchStartBuild,
  useBuildResolvedListener,
} from '../ChatBox/StartBuildingButton';
import { CreditRefillNotice } from '../CreditRefillNotice';
import { usePlanningActionSelection } from '../planningActionSelection';
import { useToast } from '../Toast/Toast';

import { continueActionPresentation } from './continueActionPresentation';
import { MessageIdentityLabel } from './MessageIdentityLabel';
import { PhaseTaskPicker } from './PhaseTaskPicker';
import {
  isSelectablePhaseTask,
  phaseTaskOptions,
  usePhaseBuildSelectionPresentation,
} from './phaseTaskSelectionStore';
import { ensureTaskSelectionPrompt } from './taskSelectionPrompt';
import { recordActionClick, recordUserActionClick, useActionClick } from './useActionBarClicks';
import {
  usePhaseTaskSelectionSummary,
  usePhaseTaskSelectionSummaryVersion,
} from './usePhaseTaskSelectionSummary';

export interface ActionOverviewEntry {
  kind: 'action';
  id: string;
  content: string;
  phase: 'user_flow' | 'first_page' | 'all_pages' | 'review_build' | 'insufficient_credits' | 'auto' | 'agent_suggestion' | 'artifact_updates';
  created_at: string;
  project_id?: string | null;
  session_id?: string | null;
  isNew?: boolean;
  wasContinued?: boolean;
  allowed_buttons?: string[] | null;
  buttons?: SupervisorButton[] | null;
  clicked_button?: string | null;
  clicked_buttons?: string[] | null;
}

/** Replace **bold** with <strong>bold</strong> */
const renderBold = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((p, i) => (
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  ));
};

interface BtnProps {
  entryId?: string;
  phase?: string;
  projectId?: string | null;
  sessionId?: string | null;
  selected?: boolean;
}

type BillingBtnProps = BtnProps & { creditShortfall?: number };
type PlanningBillingSelection = Pick<ReturnType<typeof usePlanningActionSelection>, 'selection' | 'rows'>;

const selectedBillingRow = (row: PlanningBillingSelection['rows'][number]): boolean => (
  row.checked || (row.indeterminate && row.task?.planning_metadata?.artifact_type === 'stage')
);

const useBillingButtonShortfall = (projectId?: string | null, shortfall?: number): number => {
  const status = useDesignCreditStatus(shortfall === undefined ? projectId : null);

  return shortfall ?? designCreditShortfall(status) ?? 0;
};

/** An explicit page selection must not inherit costs from other blocked pages. */
export function selectedDesignCreditShortfall(
  data: DesignPagesResponse | undefined,
  selection: PlanningBillingSelection,
  availableCredits?: number,
): number {
  const shortfall = availableCredits === undefined ? designCreditShortfall(data?.credit_status) ?? 0
    : getCreditShortfall({ required: data?.credit_status?.required_credits, balance: availableCredits }) ?? 0;

  if (!shortfall || !selection.selection) return shortfall;
  if (!selection.selection.task_ids.length) {
    return selection.selection.stages.some((stage) => stage.startsWith('design_'))
      ? shortfall : 0;
  }
  const pageNames = new Set(selection.rows.filter((row) => (
    selection.selection?.task_ids.includes(row.id) && row.task?.planning_metadata?.artifact_type === 'design_page'
  )).map((row) => row.task?.planning_metadata?.artifact_name));
  const required = data?.design_pages.filter((page) => pageNames.has(page.page_name))
    .reduce((total, page) => total + (page.credit_required ?? 0), 0) ?? 0;

  // The server aggregate also covers legacy pages whose per-page quote is absent.
  return data?.credit_status?.blocked_pages?.length
    && data.credit_status.blocked_pages.every((name) => pageNames.has(name)) ? shortfall
    : Math.max(0, Number((required - (availableCredits ?? data?.credit_status?.available_credits ?? 0)).toFixed(9)));
}

/** Captured planning quotes use the same project billing source as workspace work. */
export function selectedPlanningCreditShortfall(
  state: PhaseBuildExecutionState | null,
  selection: PlanningBillingSelection,
): number {
  if (!state || state.is_unlimited || !selection.selection) return 0;
  const required = selection.rows.reduce((total, row) => (
    selectedBillingRow(row) && row.task && !row.task.planning_credit_charged
      && !row.task.planning_metadata?.credit_charge_consumed && hasPlanningTaskCredit(row.task)
      ? total + planningTaskCreditAmount(row.task) : total
  ), 0);

  return Math.max(0, Number((required - state.available_credits).toFixed(9)));
}

const selectedPageCreditRequirement = (
  data: DesignPagesResponse | undefined, selection: PlanningBillingSelection,
): number | undefined => {
  const rows = selection.rows.filter((row) => selection.selection?.task_ids.includes(row.id)
    && row.task?.planning_metadata?.artifact_type === 'design_page');
  const pages = rows.map((row) => data?.design_pages.find((page) => (
    page.page_name === row.task?.planning_metadata?.artifact_name
  )));

  return pages.length && pages.every((page) => typeof page?.credit_required === 'number')
    ? pages.reduce((total, page) => total + page!.credit_required!, 0) : undefined;
};

const selectedPageBillingShortfall = (
  state: PhaseBuildExecutionState, pageRequired: number, selection: PlanningBillingSelection,
): number => {
  const stageFee = selectedPlanningCreditShortfall({ ...state, available_credits: 0 }, {
    ...selection, rows: selection.rows.filter((row) => row.task?.planning_metadata?.artifact_type === 'stage'),
  });

  return Math.max(0, Number((pageRequired + stageFee - state.available_credits).toFixed(9)));
};

/** A captured quote (including zero) takes precedence over legacy page estimates. */
export function selectedPlanningBillingShortfall(
  state: PhaseBuildExecutionState | null,
  data: DesignPagesResponse | undefined,
  selection: PlanningBillingSelection,
): number {
  if (!state || state.is_unlimited) return 0;
  const pageRequired = selectedPageCreditRequirement(data, selection);
  const hasQuote = selection.rows.some((row) => selectedBillingRow(row)
    && row.task?.planning_metadata?.credit_price_captured === true);

  if (pageRequired !== undefined) return selectedPageBillingShortfall(state, pageRequired, selection);

  return hasQuote ? selectedPlanningCreditShortfall(state, selection)
    : selectedDesignCreditShortfall(data, selection, state.available_credits);
}

const DoneBtn: React.FC<{ label: string }> = ({ label }) => (
  <button className="ai-overview-action-btn ai-overview-action-btn--done" disabled type="button">
    <i className="fa-solid fa-check" /> {label}
  </button>
);

export function PhaseCreditShortfallText({
  state,
}: {
  state: PhaseBuildExecutionState | null;
}) {
  if (!state || state.credit_shortfall <= 0) return null;

  const affordableCount = state.affordable_task_count ?? 0;
  const deferredCount = state.deferred_task_count ?? 0;
  const partialCopy = state.can_start
    ? ` ${affordableCount} task${affordableCount === 1 ? '' : 's'} can start now; `
      + `${deferredCount} will move to Backlog.`
    : '';

  return (
    <span className="ai-overview-phase-credit-copy">
      {formatPhaseCredits(state.credit_shortfall)} more credits needed
      {' '}({formatPhaseCredits(state.available_credits)} available of{' '}
      {formatPhaseCredits(state.required_credits)} required).
      {partialCopy}
    </span>
  );
}

export function PhaseCreditBillingActions({
  state,
  creditShortfall = state?.credit_shortfall ?? 0,
  entryId,
  projectId,
  sessionId,
  phase = 'review_build',
}: {
  state: PhaseBuildExecutionState | null;
  creditShortfall?: number;
  entryId?: string;
  projectId?: string | null;
  sessionId?: string | null;
  phase?: string;
}) {
  const props = { entryId, projectId, sessionId, phase, creditShortfall };

  return <><ActionAddCreditBtn {...props} /><ActionUpgradeBtn {...props} /></>;
}

export function ActionReviewBtn({ entryId, phase = '', projectId, sessionId }: BtnProps = {}) {
  const [loading, setLoading] = useState(false);
  const [done, markDone] = useActionClick(entryId, phase, 'review', projectId, sessionId);

  useEffect(() => {
    if (!loading) return undefined;
    const onReady = () => setLoading(false);

    window.addEventListener('review_wizard_ready', onReady);

    return () => window.removeEventListener('review_wizard_ready', onReady);
  }, [loading]);

  if (done) return <DoneBtn label="Reviewed" />;

  return (
    <button
      className="ai-overview-action-btn ai-overview-action-btn--review"
      disabled={loading} type="button"
      onClick={() => {
        void recordUserActionClick(entryId, phase, 'review', 'Review', projectId, sessionId);
        setLoading(true);
        markDone();
        window.dispatchEvent(new globalThis.CustomEvent('trigger_review_mode', { detail: { phase } }));
      }}
    >{loading
        ? <><span className="ai-overview-action-spinner" /> Reviewing...</>
        : 'Review'}
    </button>
  );
}

type ResumePhase = 'user_flow' | 'first_page' | 'all_pages' | 'review_build' | 'auto';

const ContinueDoneBtn = ({ button }: { button?: SupervisorButton }) => (
  <button className="ai-overview-action-btn ai-overview-action-btn--done" disabled type="button">
    <i className="fa-solid fa-check" /> {button?.doneLabel || button?.done_label || 'Continued'}
  </button>
);

const ContinueSpinnerBtn = ({ label = 'Continuing...' }: { label?: string }) => (
  <button
    className="ai-overview-action-btn ai-overview-action-btn--continue ai-overview-action-btn--selected ai-overview-action-btn--continuing"
    disabled
    type="button"
    aria-busy="true"
  >
    <BrandPendulumMark animated={false} className="ai-overview-action-spinner" /> {label}
  </button>
);

interface ResumeResolvedDetail { ok?: boolean; entryId?: string | null }

// eslint-disable-next-line max-lines-per-function
function useDesignResumeState(initialDone?: boolean, entryId?: string, onAccepted?: () => void, projectId?: string | null) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(!!initialDone);
  const [clicked, setClicked] = useState(false);
  const [retryable, setRetryable] = useState(false);
  const advanceObservedRef = useRef(!!initialDone);
  const pendingRef = useRef(false);
  const attemptedRef = useRef(false);
  const terminalFailureRef = useRef(false);

  // Refresh hydration can initially preserve a stored click while downstream
  // artifact queries load, then discover that the attempted advance never
  // produced its artifact. The same transition also happens when a run starts
  // successfully but later fails: once the parent has observed the active run,
  // a return to false means the local optimistic click must become retryable.
  useEffect(() => {
    if (loading) return;
    if (terminalFailureRef.current) {
      if (initialDone) return;
      terminalFailureRef.current = false;
    }
    if (initialDone) {
      advanceObservedRef.current = true;
      setRetryable(false);
      setDone(true);

      return;
    }
    if (!clicked || advanceObservedRef.current) {
      if (advanceObservedRef.current) setRetryable(true);
      advanceObservedRef.current = false;
      setClicked(false);
      setDone(false);
    }
  }, [clicked, initialDone, loading]);

  useEffect(() => {
    const resolved = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<ResumeResolvedDetail>).detail;

      // Resolutions scoped to another gate entry are not ours; plain events
      // (legacy dispatchers without entryId) resolve every listener.
      if (detail?.entryId && detail.entryId !== entryId) return;
      if (!pendingRef.current) return;
      pendingRef.current = false;
      setLoading(false);
      // ok:false — resume failed or was credit-blocked; stay actionable.
      if (detail?.ok === false) {
        setClicked(false);
        setDone(false);
        setRetryable(true);
        return;
      }
      setRetryable(false);
      onAccepted?.();
      setDone(true);
    };

    window.addEventListener('design_resume_resolved', resolved);

    return () => window.removeEventListener('design_resume_resolved', resolved);
  }, [entryId, onAccepted]);

  useEffect(() => {
    if (!projectId) return undefined;

    return eventService.subscribe('planning_complete', (data: unknown) => {
      const detail = data as { project_id?: string; status?: string } | null;

      if (!detail || detail.project_id !== projectId || !attemptedRef.current) return;
      if (!['failed', 'error', 'cancelled', 'paused_for_review'].includes(detail.status || '')) return;
      // Admission is not completion. Restore this mounted control even when
      // the parent never observed the short-lived running state.
      attemptedRef.current = false;
      pendingRef.current = false;
      terminalFailureRef.current = true;
      advanceObservedRef.current = false;
      setLoading(false);
      setClicked(false);
      setDone(false);
      setRetryable(true);
    });
  }, [projectId]);

  const startLoading = useCallback(() => {
    if (pendingRef.current) return false;
    attemptedRef.current = true;
    terminalFailureRef.current = false;
    pendingRef.current = true;
    setClicked(true);
    setLoading(true);
    return true;
  }, []);

  return { loading, startLoading, done, clicked, retryable };
}

/** Continue button for planning review gates. */
export function ActionContinueBtn(
  {
    entryId, resolutionId, phase, projectId, sessionId, initialDone, isLast,
    button, selected, busyLabel,
    persistedClickResolved = true, displayLabel, creditShortfall = 0, refreshCreditShortfall,
  }: {
    entryId?: string;
    resolutionId?: string;
    phase?: ResumePhase;
    projectId?: string | null;
    sessionId?: string | null;
    initialDone?: boolean;
    isLast?: boolean;
    button?: SupervisorButton;
    selected?: boolean;
    busyLabel?: string;
    persistedClickResolved?: boolean;
    displayLabel?: string;
    creditShortfall?: number;
    refreshCreditShortfall?: () => Promise<number | undefined>;
  } = {},
) {
  const resumeStateId = resolutionId ?? entryId;
  const [retryError, setRetryError] = useState<string | null>(null);
  const toast = useToast();
  const [recordedClick, markClicked] = useActionClick(
    entryId, phase || '', 'continue', projectId, sessionId,
  );
  const { loading, startLoading, done, clicked, retryable } = useDesignResumeState(
    initialDone, resumeStateId, markClicked, projectId,
  );
  const taskSelection = usePlanningActionSelection({
    projectId, entryId, phase, buttons: button ? [button] : undefined, current: isLast !== false,
  });
  const baseLabel = button?.label || 'Continue';
  const activeLabel = taskSelection.labelFor(displayLabel || baseLabel);
  const actionLabel = taskSelection.labelFor(baseLabel);
  const persistedSelection = !retryable && persistedClickResolved && (!!selected || recordedClick);
  const presentation = continueActionPresentation({
    loading,
    done,
    clickedThisMount: clicked,
    isLast,
    selected: persistedSelection,
  });
  const current = isLast !== false;
  const enabled = presentation === 'active' && button?.enabled !== false && !taskSelection.disabled;
  const handleClick = useCallback(async () => {
    if (!enabled) return;
    if (!startLoading()) return;
    if (creditShortfall > 0) {
      // A cached shortfall is a hint. Payment may have completed since it was
      // fetched; a failed refresh leaves admission to the server.
      const currentShortfall = refreshCreditShortfall
        ? await refreshCreditShortfall().catch(() => undefined) : creditShortfall;
      if (currentShortfall && currentShortfall > 0) {
        signalInsufficientCredits(currentShortfall);
        window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', {
          detail: { ok: false, entryId: resumeStateId },
        }));
        return;
      }
    }
    setRetryError(null);
    let planningActionInvocationId = taskSelection.preparedInvocationId;
    try {
      if (!planningActionInvocationId) {
        const recordedInvocationId = await recordUserActionClick(
          entryId, phase || '', 'continue', actionLabel, projectId, sessionId,
          taskSelection.selection ? {
            required: true, planningSelection: taskSelection.selection, baseLabel,
            onRecorded: (id) => {
              planningActionInvocationId = id;
              taskSelection.markPrepared(id, actionLabel);
            },
          } : (/^retry\b/i.test(activeLabel) ? { required: true } : undefined),
        );

        planningActionInvocationId = recordedInvocationId ?? planningActionInvocationId;
      }
      if (!planningActionInvocationId) throw new Error('Missing planning action invocation.');
    } catch (error) {
      const reason = planningActionErrorMessage(error, 'The selected work could not be prepared. Please try again.');

      toast.warning(reason);
      setRetryError(reason);
      window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', {
        detail: { ok: false, entryId: resumeStateId },
      }));

      return;
    }
    window.dispatchEvent(new globalThis.CustomEvent('resume_design_continue', {
      detail: {
        phase,
        artifactType: phase === 'user_flow' ? 'user-flow' : 'design',
        entryId: resumeStateId,
        ...(planningActionInvocationId ? { planningActionInvocationId } : {}),
      },
    }));
  }, [
    actionLabel, activeLabel, creditShortfall, refreshCreditShortfall, toast, enabled, entryId, phase, projectId, resumeStateId,
    sessionId, setRetryError, startLoading, baseLabel, taskSelection.selection,
    taskSelection.preparedInvocationId, taskSelection.markPrepared,
  ]);

  useEffect(() => {
    if (!projectId) return undefined;

    return registerPlanningAction({
      projectId,
      sessionId: sessionId ?? null,
      entryId: taskSelection.actionIdentity ?? entryId ?? resolutionId ?? `system:${phase ?? 'auto'}:${button?.key ?? 'continue'}`,
      buttonKey: 'continue',
      label: activeLabel,
      enabled,
      current,
      invoke: handleClick,
    });
  }, [
    activeLabel, button?.key, current, enabled, entryId, handleClick,
    phase, projectId, resolutionId, sessionId, taskSelection.actionIdentity,
  ]);

  if (presentation === 'loading') return <ContinueSpinnerBtn label={busyLabel} />;
  if (presentation === 'done') return <ContinueDoneBtn button={button} />;

  return (
    <>
      <button
        className={`ai-overview-action-btn ai-overview-action-btn--continue${persistedSelection
          ? ' ai-overview-action-btn--selected'
          : ''}`}
        disabled={!enabled}
        type="button"
        aria-busy={loading || undefined}
        onClick={handleClick}
      >{activeLabel}
      </button>
      {retryError && (
        <span className="ai-overview-action-error" data-action-layout-message="true" role="alert">
          {retryError}
        </span>
      )}
    </>
  );
}

/** Accept-proposed-SRD button. Commits the pending diff via the accept
 *  endpoint; the backend broadcasts a `requirements` SSE which flips the
 *  panel back to idle, and re-enqueues the planning pipeline so the next
 *  agent runs — the same UX as Continue. */
export function ActionAcceptSrdBtn(
  { entryId, projectId, sessionId, isLast }:
  { entryId?: string; projectId: string; sessionId?: string | null; isLast?: boolean },
) {
  const [loading, setLoading] = useState(false);
  const [acceptProposal] = useAcceptPendingRequirementsProposalMutation();
  const [done, markClicked] = useActionClick(entryId, 'srd_proposal', 'accept_srd');

  useEffect(() => {
    if (!loading) return undefined;
    const onResolved = (event: Event) => {
      const ce = event as globalThis.CustomEvent<{ project_id?: string }>;

      if (ce.detail?.project_id && ce.detail.project_id !== projectId) return;
      setLoading(false);
    };

    window.addEventListener('srd_proposal_resolved', onResolved);

    return () => window.removeEventListener('srd_proposal_resolved', onResolved);
  }, [loading, projectId]);

  const onClick = useCallback(async () => {
    setLoading(true);
    await recordUserActionClick(
      entryId, 'srd_proposal', 'accept_srd', 'Accept', projectId, sessionId,
    );
    try {
      await acceptProposal({ projectId, sessionId }).unwrap();
    } catch {
      setLoading(false);

      return;
    }
    markClicked();
    window.dispatchEvent(new globalThis.CustomEvent(
      'srd_proposal_resolved', { detail: { project_id: projectId, kind: 'accept' } },
    ));
  }, [acceptProposal, entryId, projectId, sessionId, markClicked]);

  if (done && !loading) return <DoneBtn label="Accepted" />;
  if (loading) {
    return (
      <button className="ai-overview-action-btn ai-overview-action-btn--continue" disabled type="button">
        <BrandPendulumMark animated={false} className="ai-overview-action-spinner" /> Accepting...
      </button>
    );
  }

  return (
    <button
      className="ai-overview-action-btn ai-overview-action-btn--accept-srd"
      disabled={!isLast} type="button" onClick={onClick}
    >
      Accept
    </button>
  );
}

/** Reject-proposed-SRD button. Discards the pending diff; the cycle stops
 *  at the SRD step until the user triggers a new run. */
export function ActionRejectSrdBtn(
  { entryId, projectId, sessionId, isLast }:
  { entryId?: string; projectId: string; sessionId?: string | null; isLast?: boolean },
) {
  const [loading, setLoading] = useState(false);
  const [rejectProposal] = useRejectPendingRequirementsProposalMutation();
  const [done, markClicked] = useActionClick(entryId, 'srd_proposal', 'reject_srd');

  const onClick = useCallback(async () => {
    setLoading(true);
    await recordUserActionClick(
      entryId, 'srd_proposal', 'reject_srd', 'Reject', projectId, sessionId,
    );
    try {
      await rejectProposal(projectId).unwrap();
    } catch {
      setLoading(false);

      return;
    }
    markClicked();
    setLoading(false);
    window.dispatchEvent(new globalThis.CustomEvent(
      'srd_proposal_resolved', { detail: { project_id: projectId, kind: 'reject' } },
    ));
  }, [rejectProposal, entryId, projectId, sessionId, markClicked]);

  if (done && !loading) return <DoneBtn label="Rejected" />;
  if (loading) {
    return (
      <button className="ai-overview-action-btn ai-overview-action-btn--reject-srd" disabled type="button">
        <span className="ai-overview-action-spinner" /> Rejecting...
      </button>
    );
  }

  return (
    <button
      className="ai-overview-action-btn ai-overview-action-btn--reject-srd"
      disabled={!isLast} type="button" onClick={onClick}
    >
      Reject
    </button>
  );
}

function useSuccessfulBuildClick(
  clickIdRef: React.MutableRefObject<string | null>,
  setLoading: (loading: boolean) => void,
  markDone: () => void,
) {
  const handleBuildResolved = useCallback((detail: BuildFlowResolvedDetail) => {
    if (isSuccessfulBuildResolution(detail, clickIdRef.current)) markDone();
  }, [clickIdRef, markDone]);

  useBuildResolvedListener(clickIdRef, setLoading, handleBuildResolved);
}

export function ActionBuildBtn({
  entryId,
  phase = '',
  projectId,
  sessionId,
  selected,
}: BtnProps = {}) {
  const [loading, setLoading] = useState(false);
  const promptPendingRef = useRef(false);
  const [done, markDone] = useActionClick(entryId, phase, 'build', projectId, sessionId);
  const clickIdRef = useRef<string | null>(null);
  const toast = useToast();
  const phaseBuild = usePhaseBuildState(projectId);
  const buildSelection = usePhaseBuildSelectionPresentation(
    projectId,
    phaseBuild.state,
    phaseBuild.isLoading,
  );
  const selectionSummaryId = usePhaseTaskSelectionSummaryVersion(
    projectId,
    buildSelection.phaseId,
    buildSelection.selectedTaskIds,
  );
  const creditShortfall = getPhaseActionCreditShortfall(
    phaseBuild.state,
    buildSelection.creditShortfall,
    buildSelection.active,
  );
  const buildEnabled = isPhaseBuildActionEnabled(phaseBuild.state);
  const selectionRequired = buildSelection.active
    && (buildSelection.selectedTaskIds?.length ?? 0) === 0;
  const selectionPromptEnabled = selectionRequired
    && Boolean(phaseBuild.state?.current_phase)
    && !phaseBuild.state?.all_completed
    && phaseBuild.state?.blocking_reason !== 'build_in_progress';
  const disabledForNonCreditReason = buildSelection.disabled
    && creditShortfall <= 0
    && !selectionPromptEnabled;

  useSuccessfulBuildClick(clickIdRef, setLoading, markDone);

  if (done && phaseBuild.state?.all_completed) return <DoneBtn label="Built" />;

  return (
    <>
      <button
        className={`ai-overview-action-btn ai-overview-action-btn--build${selected
          ? ' ai-overview-action-btn--selected'
          : ''}`}
        disabled={
          loading
          || phaseBuild.isLoading
          || disabledForNonCreditReason
          || (!buildEnabled && creditShortfall <= 0 && !selectionPromptEnabled)
        }
        title={buildSelection.disabledReason ?? getPhaseBuildTitle(phaseBuild.state)}
        type="button"
        onClick={() => {
          if (selectionPromptEnabled) {
            toast.warning('Select at least one task to build.');
            if (projectId && buildSelection.phaseId && !promptPendingRef.current) {
              promptPendingRef.current = true;
              void ensureTaskSelectionPrompt(
                projectId,
                buildSelection.phaseId,
                sessionId,
              ).catch(() => {
                toast.warning('The task selector could not be restored. Please try again.');
              }).finally(() => {
                promptPendingRef.current = false;
              });
            }

            return;
          }
          void recordUserActionClick(
            entryId, phase, 'build', buildSelection.label, projectId, sessionId,
          );
          if (creditShortfall > 0) {
            signalInsufficientCredits(creditShortfall);

            return;
          }
          setLoading(true);
          // Don't navigate to tasks tab - stay in AI overview
          dispatchStartBuild(
            clickIdRef,
            buildSelection.phaseId,
            buildSelection.selectedTaskIds,
            selectionSummaryId,
          );
        }}
      >{loading
          ? <><span className="ai-overview-action-spinner" /> {getPhaseBuildBusyLabel(phaseBuild.state)}</>
          : buildSelection.label}
      </button>
    </>
  );
}

export function ActionAddCreditBtn({
  entryId, phase = '', projectId, sessionId, creditShortfall: selectedShortfall,
}: BillingBtnProps = {}) {
  const creditShortfall = useBillingButtonShortfall(projectId, selectedShortfall);
  const [showModal, setShowModal] = useState(false);
  const [, markDone] = useActionClick(entryId, phase, 'addcredit', projectId, sessionId);

  useEffect(() => {
    if (!showModal) return undefined;

    const onSuccess = () => {
      markDone();
      setShowModal(false);
    };

    window.addEventListener(BILLING_TOPUP_SUCCESS_EVENT, onSuccess);

    return () => window.removeEventListener(BILLING_TOPUP_SUCCESS_EVENT, onSuccess);
  }, [markDone, showModal]);

  if (creditShortfall <= 0 && !showModal) return null;

  return (
    <>
      {creditShortfall > 0 && <button
        className="ai-overview-action-btn ai-overview-action-btn--add-credit"
        type="button"
        onClick={() => {
          void recordUserActionClick(
            entryId, phase, 'addcredit', 'Add Credits', projectId, sessionId,
          );
          setShowModal(true);
        }}
      >
        Add Credits
      </button>}
      {showModal && (
        <BillingModal
          initialView="credits"
          initialCreditAmount={getTopUpCreditAmount({ shortfall: creditShortfall })}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

/** Live "N more credits needed" line for a design credit gate. */
export function DesignCreditShortfallText({ projectId }: { projectId?: string | null }) {
  const status = useDesignCreditStatus(projectId);
  const copy = designCreditCopy(status);

  if (!copy) return null;

  return (
    <span className="ai-overview-phase-credit-copy ai-overview-phase-credit-copy--gate">
      {copy}
    </span>
  );
}

/** Compute a fly-to-chat-input ghost geometry. Mirrors the helper in
 *  ItemApprovalBar so the Specify Edits animation looks identical whether
 *  it's launched from an artifact approval bar or from a Tech Lead action
 *  bubble. */
interface FlyGhostState { x: number; y: number; w: number; h: number; dx: number; dy: number }

const computeFlyToChatInputForBtn = (src: HTMLElement): FlyGhostState | null => {
  const input = document.querySelector<HTMLElement>('.text-input');
  if (!input) return null;
  const srcRect = src.getBoundingClientRect();
  const inputRect = input.getBoundingClientRect();
  const cs = window.getComputedStyle(input);
  const padLeft = parseFloat(cs.paddingLeft) || 0;
  const padTop = parseFloat(cs.paddingTop) || 0;
  const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
  const borderTop = parseFloat(cs.borderTopWidth) || 0;
  const lineH = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 16) * 1.4;
  const caretX = inputRect.left + borderLeft + padLeft + 2;
  const caretY = inputRect.top + borderTop + padTop + lineH / 2;
  const dx = caretX - (srcRect.left + srcRect.width / 2);
  const dy = caretY - (srcRect.top + srcRect.height / 2);
  return {
    x: srcRect.left, y: srcRect.top, w: srcRect.width, h: srcRect.height, dx, dy,
  };
};

/** "Specify Edits" button for Tech Lead action bubbles (review_build /
 *  all_pages / first_page gates). Mirrors the artifact-approval bar's
 *  Specify Edits behaviour: pre-fills the chat input with a labelled stub
 *  and flies the button into the input as a visual handoff. The user
 *  finishes the prompt inline and sends; the supervisor routes the message
 *  to the right agent. */
export function ActionSpecifyEditsBtn({
  entryId,
  phase = '',
  projectId,
  sessionId,
  selected,
  label = 'page designs',
}: BtnProps & { label?: string } = {}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [flyGhost, setFlyGhost] = useState<FlyGhostState | null>(null);
  const [recordedClick, markClicked] = useActionClick(
    entryId, phase, 'specify_edits', projectId, sessionId,
  );

  const handleSpecifyEdits = useCallback(() => {
    const prefill = `Please make the following changes to the ${label}:\n\n`;
    window.dispatchEvent(new globalThis.CustomEvent('prefill_chat_input', {
      detail: { content: prefill },
    }));
  }, [label]);

  const handleClick = () => {
    void recordUserActionClick(
      entryId, phase, 'specify_edits', 'Specify Edits', projectId, sessionId,
    );
    markClicked();
    const btn = btnRef.current;
    if (!btn) { handleSpecifyEdits(); return; }
    const state = computeFlyToChatInputForBtn(btn);
    if (!state) { handleSpecifyEdits(); return; }
    setFlyGhost(state);
  };

  const handleGhostEnd = () => {
    setFlyGhost(null);
    handleSpecifyEdits();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`ai-overview-action-btn ai-overview-action-btn--edit${selected || recordedClick
          ? ' ai-overview-action-btn--selected'
          : ''}`}
        style={flyGhost ? { visibility: 'hidden' } : undefined}
        onClick={handleClick}
        disabled={!!flyGhost}
        title={`Specify edits for ${label}`}
      >
        Specify Edits
      </button>
      {flyGhost && createPortal(
        <div
          className="ai-overview-action-btn ai-overview-action-btn--edit ai-overview-action-btn--fly-ghost"
          style={{
            position: 'fixed',
            left: flyGhost.x, top: flyGhost.y,
            width: flyGhost.w, height: flyGhost.h,
            margin: 0, pointerEvents: 'none', zIndex: 9999,
            ['--fly-dx' as string]: `${flyGhost.dx}px`,
            ['--fly-dy' as string]: `${flyGhost.dy}px`,
          }}
          onAnimationEnd={handleGhostEnd}
        >
          Specify Edits
        </div>,
        document.body,
      )}
    </>
  );
}

export function ActionExportBtn({ entryId, phase = '', projectId, sessionId }: BtnProps = {}) {
  return (
    <button
      className="ai-overview-action-btn ai-overview-action-btn--export"
      type="button"
      onClick={() => {
        void recordUserActionClick(
          entryId, phase, 'export', 'Export Proposal', projectId, sessionId,
        );
        void recordActionClick(entryId, phase, 'export', projectId, sessionId);
        window.dispatchEvent(new globalThis.CustomEvent('trigger_export_proposal', {
          detail: { stayOnOverview: true },
        }));
      }}
    >
      Export Proposal
    </button>
  );
}

export function ActionUpgradeBtn({
  entryId, phase = '', projectId, sessionId, creditShortfall: selectedShortfall,
}: BillingBtnProps = {}) {
  const creditShortfall = useBillingButtonShortfall(projectId, selectedShortfall);
  const [showModal, setShowModal] = useState(false);
  const [, markDone] = useActionClick(entryId, phase, 'upgrade', projectId, sessionId);

  useEffect(() => {
    if (!showModal) return undefined;

    const onSuccess = () => {
      markDone();
      setShowModal(false);
    };

    window.addEventListener(BILLING_UPGRADE_SUCCESS_EVENT, onSuccess);

    return () => window.removeEventListener(BILLING_UPGRADE_SUCCESS_EVENT, onSuccess);
  }, [markDone, showModal]);

  if (creditShortfall <= 0 && !showModal) return null;

  return (
    <>
      {creditShortfall > 0 && <button
        className="ai-overview-action-btn ai-overview-action-btn--upgrade"
        type="button"
        onClick={() => {
          void recordUserActionClick(
            entryId, phase, 'upgrade', 'Upgrade', projectId, sessionId,
          );
          setShowModal(true);
        }}
      >
        Upgrade
      </button>}
      {showModal && <BillingModal initialView="plans" onClose={() => setShowModal(false)} />}
    </>
  );
}

export function ActionCreditContinueBtn(
  {
    entryId, projectId, sessionId, initialDone, isLast, button, selected,
    persistedClickResolved = true, displayLabel, creditShortfall = 0, refreshCreditShortfall,
  }: {
    entryId?: string;
    projectId?: string | null;
    sessionId?: string | null;
    initialDone?: boolean;
    isLast?: boolean;
    button?: SupervisorButton;
    selected?: boolean;
    persistedClickResolved?: boolean;
    displayLabel?: string;
    creditShortfall?: number;
    refreshCreditShortfall?: () => Promise<number | undefined>;
  },
) {
  const toast = useToast();
  const [recordedClick, markClicked] = useActionClick(
    entryId,
    'insufficient_credits',
    'credit_continue',
    projectId,
    sessionId,
  );
  const { loading, startLoading, done, clicked, retryable } = useDesignResumeState(
    initialDone, entryId, markClicked, projectId,
  );
  const persistedSelection = !retryable && persistedClickResolved && (!!selected || recordedClick);
  const presentation = continueActionPresentation({
    loading,
    done,
    clickedThisMount: clicked,
    isLast,
    selected: persistedSelection,
  });
  const current = isLast !== false;
  const taskSelection = usePlanningActionSelection({
    projectId, entryId, phase: 'insufficient_credits', buttons: button ? [button] : undefined, current,
  });
  const baseLabel = button?.label || 'Continue';
  const activeLabel = taskSelection.labelFor(displayLabel || baseLabel);
  const actionLabel = taskSelection.labelFor(baseLabel);
  const enabled = presentation === 'active' && button?.enabled !== false && !taskSelection.disabled;
  const handleClick = useCallback(async () => {
    if (!enabled) return;
    if (!startLoading()) return;
    if (creditShortfall > 0) {
      // A cached shortfall is a hint. Payment may have completed since it was
      // fetched; a failed refresh leaves admission to the server.
      const currentShortfall = refreshCreditShortfall
        ? await refreshCreditShortfall().catch(() => undefined) : creditShortfall;
      if (currentShortfall && currentShortfall > 0) {
        signalInsufficientCredits(currentShortfall);
        window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', {
          detail: { ok: false, entryId: entryId },
        }));
        return;
      }
    }
    let planningActionInvocationId = taskSelection.preparedInvocationId;
    try {
      if (!planningActionInvocationId) {
        const recordedInvocationId = await recordUserActionClick(
          entryId, 'insufficient_credits', 'credit_continue', actionLabel, projectId, sessionId,
          taskSelection.selection ? {
            required: true, planningSelection: taskSelection.selection, baseLabel,
            onRecorded: (id) => {
              planningActionInvocationId = id;
              taskSelection.markPrepared(id, actionLabel);
            },
          } : undefined,
        );

        planningActionInvocationId = recordedInvocationId ?? planningActionInvocationId;
      }
      if (!planningActionInvocationId) throw new Error('Missing planning action invocation.');
    } catch {
      toast.warning('The selected work could not be prepared. Refresh the actions and try again.');
      window.dispatchEvent(new globalThis.CustomEvent('design_resume_resolved', { detail: { ok: false, entryId } }));
      return;
    }
    window.dispatchEvent(new globalThis.CustomEvent('resume_design_continue', {
      detail: {
        phase: supervisorButtonResumePhase(button), artifactType: 'design', entryId,
        ...(planningActionInvocationId ? { planningActionInvocationId } : {}),
      },
    }));
  }, [
    actionLabel, activeLabel, creditShortfall, refreshCreditShortfall, toast, baseLabel, button, enabled, entryId, projectId, sessionId, startLoading,
    taskSelection.selection, taskSelection.preparedInvocationId, taskSelection.markPrepared,
  ]);

  useEffect(() => {
    if (!projectId) return undefined;

    return registerPlanningAction({
      projectId,
      sessionId: sessionId ?? null,
      entryId: taskSelection.actionIdentity ?? entryId ?? 'system:insufficient_credits:credit_continue',
      buttonKey: 'credit_continue',
      label: activeLabel,
      enabled,
      current,
      invoke: handleClick,
    });
  }, [activeLabel, current, enabled, entryId, handleClick, projectId, sessionId, taskSelection.actionIdentity]);

  if (presentation === 'loading') return <ContinueSpinnerBtn />;
  if (presentation === 'done') return <ContinueDoneBtn button={button} />;

  return (
    <button
      className={`ai-overview-action-btn ai-overview-action-btn--continue${persistedSelection
        ? ' ai-overview-action-btn--selected'
        : ''}`}
      data-attention-primary="true"
      disabled={!enabled}
      type="button"
      aria-busy={loading || undefined}
      onClick={handleClick}
    >{activeLabel}
    </button>
  );
}

/** Pick a state-appropriate prompt for the review_build bar when the
 *  artifacts it claims are ready don't actually exist yet. */
function pendingReviewBuildContent(hasArch: boolean, hasTasks: boolean): string {
  if (!hasArch && !hasTasks) return 'Page designs are ready. Click **Continue** to generate architecture and tasks.';
  if (!hasArch) return 'Page designs are ready. Click **Continue** to generate the architecture.';

  return 'Architecture is ready. Click **Continue** to generate the task plan.';
}

/** Rewrite the bubble text when an action message arrives ahead of the
 *  artifacts it claims are ready. The supervisor used to emit
 *  "Review the design and architecture and start building." even on selective
 *  runs that produced design pages but no architecture or tasks; existing
 *  projects still carry that stored message in chat history. Showing it
 *  verbatim is misleading — there's no architecture or tasks to review yet —
 *  so we substitute a state-appropriate prompt that matches the Continue
 *  button rendered alongside it. */
function useDisplayedActionContent(
  entry: ActionOverviewEntry, projectId?: string | null,
): string {
  const hasTasks = useProjectHasGeneratedTasks(projectId);
  const hasArch = useProjectHasArchitecture(projectId);
  const { currentData: planningStatus } = useGetPlanningStatusQuery(
    projectId ?? '',
    { skip: !projectId },
  );
  const taskContractComplete = isPlanningTaskContractComplete(
    planningStatus?.pm_step_status,
  );
  const isPending = entry.phase === 'review_build'
    && !(hasTasks && hasArch && taskContractComplete);
  const content = isPending
    ? pendingReviewBuildContent(hasArch, hasTasks)
    : entry.content;

  return normalizePhaseReadyTaskSummary(content);
}

const phaseReadyNumber = (content: string): number | null => {
  const match = content.match(/\bPhase\s+(\d+)\s+[—-]\s+.+\s+is ready\b/i);

  return match ? Number(match[1]) : null;
};

const usePhaseSelectionMessage = (
  entryId: string,
  projectId: string | null | undefined,
  state: PhaseBuildExecutionState | null,
  loading: boolean,
  currentPhaseMatches: boolean,
) => {
  const matchingState = currentPhaseMatches ? state : null;
  const selection = usePhaseBuildSelectionPresentation(projectId, matchingState, loading);
  const tasks = phaseTaskOptions(matchingState);
  const selectedIds = new Set(selection.selectedTaskIds ?? []);
  const selectedTasks = tasks.filter((task) => selectedIds.has(task.id));
  const hasTasks = tasks.some(isSelectablePhaseTask);
  const fallbackMessage = matchingState && hasTasks
    ? phaseSelectionPrompt(matchingState, selectedTasks)
    : null;
  const summary = usePhaseTaskSelectionSummary({
    fallbackMessage,
    projectId,
    sourceEntryKey: entryId,
    state: matchingState,
  });

  return {
    hasTasks,
    message: summary.message,
    onSelectionChange: summary.onSelectionChange,
  };
};
export const ActionEntry: React.FC<{
  entry: ActionOverviewEntry;
  formatTime: (iso: string) => string;
  isLast?: boolean;
  projectId?: string | null;
  showCreditRefill?: boolean;
}> = ({ entry, formatTime, isLast, projectId, showCreditRefill = true }) => {
  const storedContent = useDisplayedActionContent(entry, projectId);
  const readyPhase = phaseReadyNumber(storedContent);
  const useCurrentReviewPhase = readyPhase === null && entry.phase === 'review_build' && Boolean(isLast);
  const phaseBuild = usePhaseBuildState(
    readyPhase !== null || useCurrentReviewPhase ? projectId : null,
  );
  const currentPhaseMatches = readyPhase !== null
    ? phaseBuild.state?.current_phase?.phase_number === readyPhase
    : useCurrentReviewPhase && Boolean(phaseBuild.state?.current_phase);
  const phaseSelection = usePhaseSelectionMessage(
    entry.id, projectId, phaseBuild.state,
    phaseBuild.isLoading,
    currentPhaseMatches,
  );
  const currentReviewContent = useCurrentReviewPhase
    ? currentPhaseReadyMessage(phaseBuild.state) ?? storedContent
    : storedContent;
  const content = phaseSelection.message ?? formatPhaseReadyMessageForState(
    currentReviewContent,
    phaseBuild.state,
  );
  const showCreditActions = currentPhaseMatches
    && Boolean(phaseBuild.state?.credit_shortfall);
  const isCreditGate = entry.phase === 'insufficient_credits';

  return (
    <div className={`ai-overview-bubble ai-overview-action-entry${phaseSelection.hasTasks ? ' task-selection-bubble' : ''}${entry.phase === 'insufficient_credits' ? ' ai-overview-action-entry--credits' : ''}${isLast ? ' ai-overview-entry--last' : ''}`}>
      <div className="ai-overview-bubble__header">
        <MessageIdentityLabel className="ai-overview-bubble__label" name="Tech Lead Agent" />
        <span className="ai-overview-entry__time" style={{ color: '#64748b', fontSize: '10px' }}>{formatTime(entry.created_at)}</span>
      </div>
      <div className="ai-overview-action-entry__body">
        <span className="ai-overview-bubble__text" aria-live="polite">
          {renderBold(content)}
        </span>
        {phaseSelection.hasTasks && phaseBuild.state && (
          <PhaseTaskPicker
            entryId={entry.id}
            projectId={projectId}
            state={phaseBuild.state}
            onSelectionChange={phaseSelection.onSelectionChange}
          />
        )}
        {showCreditActions && (
          <div className="ai-overview-tech-lead-credit">
            <PhaseCreditShortfallText state={phaseBuild.state} />
          </div>
        )}
        {/* No divider wrapper here: this line continues the gate sentence
            directly, so it sits on the body's normal stacking gap. */}
        {isCreditGate && <DesignCreditShortfallText projectId={projectId} />}
        {showCreditRefill && isLast && (isCreditGate || showCreditActions) && (
          <CreditRefillNotice projectId={projectId} />
        )}
        {/* Action buttons live in UnifiedActionBar below the bubble — rendered
            for every action entry so the user can see their full click history. */}
      </div>
    </div>
  );
};
